import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { normalizeImages } from "@/lib/images";
import { useAuth } from "@/context/AuthContext";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, MapPin, MessageCircle, ShoppingBag, ShieldCheck, ArrowLeft, BadgeCheck, Flag, Share2, Wallet, Eye, Flame, Sparkles, Minus, Plus, Truck, Globe } from "lucide-react";
import { toast } from "sonner";

// Inline WhatsApp glyph — saves a dependency, scales cleanly with currentColor.
const WhatsAppIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.768.967-.94 1.165-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479c0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.695.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zm-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);
import PayPalCheckoutButton from "@/components/PayPalCheckoutButton";
import StarRating from "@/components/StarRating";
import ListingReviews from "@/components/listing/ListingReviews";
import { getRefForCheckout, appendRefToUrl } from "@/lib/referrals";
import { recordPlyxAttribution, getActivePlyxAttribution } from "@/lib/plyxAttribution";
import SEO from "@/components/SEO";
import { useCurrency } from "@/context/CurrencyContext";
import { rateBasisSuffix } from "@/data/rateBasis";
import ServiceEstimator from "@/components/listing/ServiceEstimator";
import ImageMagnifier from "@/components/listing/ImageMagnifier";
import { highStreetSavings } from "@/lib/highStreetSavings";
import { TrendingDown } from "lucide-react";
import { socialProofBadge } from "@/lib/socialProof";
import { SHIPPING_COUNTRIES } from "@/data/shippingCountries";

const currencySymbol = (c) => ({ USD: "$", GBP: "£", EUR: "€", CAD: "C$" }[c] || `${c} `);

const ListingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Plyx attribution: when buyer arrives via /listing/:id?plyx_v=… we forward
  // this through to checkout so the creator gets credited on a successful sale.
  // Persists 24h via localStorage so attribution survives if the buyer
  // browses other listings or comes back later in the same session.
  const urlPlyxV = searchParams.get("plyx_v") || null;
  useEffect(() => { if (urlPlyxV) recordPlyxAttribution(urlPlyxV); }, [urlPlyxV]);
  const plyxAttributionVideoId = urlPlyxV || getActivePlyxAttribution();
  const { user } = useAuth();
  const { formatPrice, isConverted, displayCurrency, userCountry } = useCurrency();
  const [listing, setListing] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [qty, setQty] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  useEffect(() => {
    const vs = listing?.variants || [];
    if (vs.length > 0) {
      setSelectedVariant((prev) =>
        prev || vs.reduce((a, b) => ((a?.price ?? Infinity) <= (b?.price ?? Infinity) ? a : b))
      );
    }
  }, [listing]);
  const effectivePrice = selectedVariant?.price ?? Number(listing?.price) ?? 0;
  // Plyx attribution chip — fetched once when arriving via a Plyx video.
  const [plyxSource, setPlyxSource] = useState(null);

  useEffect(() => {
    if (!plyxAttributionVideoId) { setPlyxSource(null); return; }
    let cancelled = false;
    api.get(`/plyx/videos/${plyxAttributionVideoId}`)
      .then((r) => {
        if (cancelled) return;
        const c = r.data?.creator;
        if (c?.handle) setPlyxSource({ handle: c.handle, display_name: c.display_name, avatar_url: c.avatar_url });
      })
      .catch(() => { /* fire-and-forget — chip just won't render */ });
    return () => { cancelled = true; };
  }, [plyxAttributionVideoId]);

  useEffect(() => {
    setLoading(true);
    api.get(`/listings/${id}`).then((r) => setListing(r.data)).catch(() => toast.error("Listing not found")).finally(() => setLoading(false));
    // Fire-and-forget social-proof signal — powers "47 viewing this week" badges.
    let sid = null;
    try {
      sid = localStorage.getItem("afri_sid");
      if (!sid) {
        sid = `s_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
        localStorage.setItem("afri_sid", sid);
      }
    } catch { /* ignore */ }
    api.post("/analytics/event", {
      event: "listing_view",
      session_id: sid || undefined,
      url: window.location.pathname,
      props: { listing_id: id },
    }).catch(() => { /* fire-and-forget */ });
  }, [id]);

  const handleContact = async () => {
    if (!user) { navigate("/login", { state: { from: `/listing/${id}` } }); return; }
    setContactLoading(true);
    try {
      const { data } = await api.post(`/conversations?listing_id=${id}`);
      navigate(`/dashboard/messages?c=${data.conversation_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start conversation");
    } finally { setContactLoading(false); }
  };

  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null); // {valid, discount, final_amount, code}
  const [promoChecking, setPromoChecking] = useState(false);

  // Live shipping (curated CJ items only) — buyer picks destination country.
  const [shipCountry, setShipCountry] = useState("");
  const [shipQuote, setShipQuote] = useState(null); // {available, shipping, currency, method, eta, message}
  const [shipQuoting, setShipQuoting] = useState(false);

  // Auto-select the buyer's country so shipping shows immediately (no manual
  // step). Falls back to UK — the main diaspora market — if geo is unknown.
  useEffect(() => {
    if (!listing?.is_curated || shipCountry) return;
    const name = (userCountry || "").trim().toLowerCase();
    const aliases = { "uk": "GB", "united kingdom": "GB", "usa": "US", "united states": "US", "america": "US" };
    let code = aliases[name];
    if (!code && name) {
      const hit = SHIPPING_COUNTRIES.find((c) => c.name.toLowerCase() === name);
      code = hit?.code;
    }
    setShipCountry(code || "GB");
  }, [listing, userCountry, shipCountry]);

  useEffect(() => {
    if (!listing?.is_curated || !shipCountry) { setShipQuote(null); return; }
    let alive = true;
    setShipQuoting(true);
    setShipQuote(null);
    (async () => {
      try {
        const { data } = await api.post("/shipping/quote", {
          listing_id: id,
          country_code: shipCountry,
          qty,
          variant_vid: selectedVariant?.vid || null,
        });
        if (alive) setShipQuote(data);
      } catch {
        if (alive) setShipQuote({ available: false, shipping: 0, message: "Could not fetch shipping rate." });
      } finally {
        if (alive) setShipQuoting(false);
      }
    })();
    return () => { alive = false; };
  }, [listing, shipCountry, qty, selectedVariant, id]);

  const checkPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoChecking(true);
    try {
      const { data } = await api.post("/promo-codes/validate", {
        code: promoCode,
        amount: listing.price,
        currency: (listing.currency || "GBP").toUpperCase(),
      });
      if (data.valid) {
        setPromoApplied(data);
        toast.success(`${data.code} applied — you save ${currencySymbol(listing.currency)}${data.discount.toFixed(2)}!`);
      } else {
        setPromoApplied(null);
        toast.error(data.reason || "Invalid code");
      }
    } catch {
      toast.error("Could not validate code");
    } finally {
      setPromoChecking(false);
    }
  };

  const handleBuy = async () => {
    if (!user) { navigate("/login", { state: { from: `/listing/${id}` } }); return; }
    if (listing.is_curated && !shipCountry) {
      toast.error("Please choose your shipping country first");
      return;
    }
    if (listing.is_curated && shipQuoting) {
      toast.error("Calculating shipping — one moment…");
      return;
    }
    setCheckoutLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        listing_id: id,
        origin_url: window.location.origin,
        referrer_id: getRefForCheckout(),
        promo_code: promoApplied?.valid ? promoApplied.code : null,
        qty,
        variant_vid: selectedVariant?.vid || null,
        ship_country: shipCountry || null,
        plyx_attribution_video_id: plyxAttributionVideoId,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout");
      setCheckoutLoading(false);
    }
  };

  if (loading) return <div className="container-narrow py-24 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin inline mr-2"/>Loading…</div>;
  if (!listing) return <div className="container-narrow py-24 text-center">Not found</div>;

  const images = normalizeImages(listing.images);
  const img = images[activeImg] || images[0] || "https://images.unsplash.com/photo-1606744824163-985d376605aa?auto=format&fit=crop&q=80&w=1200";
  const isOwner = user?.user_id === listing.owner_id;

  // ── SEO data ──────────────────────────────────────────────────────
  const sym = currencySymbol(listing.currency);
  const seoTitle = `${listing.title} · ${sym}${listing.price}`;
  const seoDesc = (listing.description || `${listing.title} — ${listing.category} on AfriMarket. ${listing.country || ""}.`).slice(0, 158);
  const heroImage = images[0] || "https://afrimarket.org/og-image.png";
  const absoluteImage = heroImage.startsWith("http") ? heroImage : `https://afrimarket.org${heroImage}`;

  // Google rich-card enrichment — fields beyond the basics required for the
  // merchant-listing experience (★ stars, price, availability, shipping).
  // Ref: https://developers.google.com/search/docs/appearance/structured-data/product
  const CONDITION_MAP = {
    new: "https://schema.org/NewCondition",
    like_new: "https://schema.org/NewCondition",
    refurbished: "https://schema.org/RefurbishedCondition",
    used: "https://schema.org/UsedCondition",
  };
  const itemCondition = CONDITION_MAP[listing.condition] || "https://schema.org/NewCondition";
  const priceValidUntil = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  })();
  const isProduct = listing.type !== "service";
  // Diaspora-first: AfriMarket serves UK / US / CA buyers, so we declare those
  // as eligible shipping destinations. Each gets a free-shipping rate row.
  const shippingDestinations = ["GB", "US", "CA"];
  const shippingDetails = shippingDestinations.map((cc) => ({
    "@type": "OfferShippingDetails",
    "shippingRate": {
      "@type": "MonetaryAmount",
      "value": "0",
      "currency": (listing.currency || "USD").toUpperCase(),
    },
    "shippingDestination": {
      "@type": "DefinedRegion",
      "addressCountry": cc,
    },
    "deliveryTime": {
      "@type": "ShippingDeliveryTime",
      "handlingTime": { "@type": "QuantitativeValue", "minValue": 1, "maxValue": 3, "unitCode": "DAY" },
      "transitTime": { "@type": "QuantitativeValue", "minValue": 3, "maxValue": 14, "unitCode": "DAY" },
    },
  }));
  const merchantReturnPolicy = {
    "@type": "MerchantReturnPolicy",
    "applicableCountry": shippingDestinations,
    "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
    "merchantReturnDays": 14,
    "returnMethod": "https://schema.org/ReturnByMail",
    "returnFees": "https://schema.org/FreeReturn",
  };

  const productLd = {
    "@context": "https://schema.org",
    "@type": isProduct ? "Product" : "Service",
    "name": listing.title,
    "description": listing.description || listing.title,
    "image": images.map((u) => (u.startsWith("http") ? u : `https://afrimarket.org${u}`)).slice(0, 6),
    "category": listing.category,
    "sku": listing.listing_id,
    "productID": listing.listing_id,
    "brand": { "@type": "Brand", "name": listing.owner_name || "AfriMarket Seller" },
    ...(isProduct && { "itemCondition": itemCondition }),
    ...(listing.rating_count > 0 && {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": listing.rating_avg,
        "reviewCount": listing.rating_count,
        "bestRating": 5,
        "worstRating": 1,
      },
    }),
    "offers": {
      "@type": "Offer",
      "url": `https://afrimarket.org/listing/${listing.listing_id}`,
      "priceCurrency": (listing.currency || "USD").toUpperCase(),
      "price": listing.price,
      "priceValidUntil": priceValidUntil,
      "availability": listing.status === "sold" ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
      "itemCondition": itemCondition,
      "seller": {
        "@type": listing.owner_verified_badge ? "Organization" : "Person",
        "name": listing.owner_name || "Verified AfriMarket Seller",
      },
      ...(isProduct && {
        "shippingDetails": shippingDetails,
        "hasMerchantReturnPolicy": merchantReturnPolicy,
      }),
    },
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://afrimarket.org/" },
      { "@type": "ListItem", "position": 2, "name": listing.type === "service" ? "Services" : "Products", "item": `https://afrimarket.org/browse?type=${listing.type}` },
      { "@type": "ListItem", "position": 3, "name": listing.category, "item": `https://afrimarket.org/browse?type=${listing.type}&category=${encodeURIComponent(listing.category || "")}` },
      { "@type": "ListItem", "position": 4, "name": listing.title, "item": `https://afrimarket.org/listing/${listing.listing_id}` },
    ],
  };

  return (
    <div className="container-narrow py-10">
      <SEO
        title={seoTitle}
        description={seoDesc}
        path={`/listing/${listing.listing_id}`}
        image={absoluteImage}
        type="product"
        jsonLd={[productLd, breadcrumbLd]}
      />
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-[#C05A3C] mb-6" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4"/> Back
      </button>

      <div className="grid lg:grid-cols-12 gap-10">
        {/* Gallery */}
        <div className="lg:col-span-7">
          {listing.video_url && (
            <div className="aspect-[4/3] rounded-lg overflow-hidden bg-black mb-3" data-testid="listing-video">
              <video
                src={listing.video_url}
                controls
                playsInline
                preload="metadata"
                poster={images[0]}
                className="w-full h-full object-contain bg-black"
              >
                Your browser doesn&apos;t support video playback.
              </video>
            </div>
          )}
          <div className="aspect-[4/3] rounded-lg overflow-hidden bg-[#F3EFE9] relative group">
            <ImageMagnifier src={img} alt={listing.title} />
            <div className="absolute bottom-2 right-2 hidden md:flex items-center gap-1 px-2 py-1 rounded-full bg-black/55 text-white text-[10px] font-semibold uppercase tracking-wider pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity" data-testid="zoom-hint">
              Hover to zoom
            </div>
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-3">
              {images.map((src, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square overflow-hidden rounded-md border-2 ${i === activeImg ? "border-[#C05A3C]" : "border-transparent"}`} data-testid={`thumb-${i}`}>
                  <img src={src} alt="" className="w-full h-full object-cover"/>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-24">
            <div className="flex items-center gap-2 mb-3">
              <Badge className="bg-[#F3EFE9] text-[#2A3D36] capitalize">{listing.type}</Badge>
              <Badge variant="outline" className="border-black/20">{listing.category}</Badge>
              <button
                type="button"
                onClick={() => {
                  const shareUrl = user ? appendRefToUrl(window.location.href, user.user_id) : window.location.href;
                  const symbol = currencySymbol(listing.currency);
                  const msg = `Check out ${listing.title} on AfriMarket — ${symbol}${listing.price}${listing.type === "service" ? "/hr" : ""}\n${shareUrl}`;
                  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
                }}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-[#25D366] ml-auto"
                data-testid="listing-whatsapp-btn"
                aria-label="Share on WhatsApp"
                title="Share on WhatsApp"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.05 21.785a9.704 9.704 0 01-4.95-1.354l-.355-.21-3.681.965.984-3.589-.231-.369a9.71 9.71 0 01-1.49-5.182c.001-5.367 4.37-9.736 9.737-9.736 2.6 0 5.043 1.013 6.882 2.852a9.66 9.66 0 012.85 6.884c-.002 5.367-4.37 9.738-9.736 9.738zm8.413-18.15A11.65 11.65 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.892-11.893a11.821 11.821 0 00-3.479-8.413z"/>
                </svg>
                WhatsApp
              </button>
              <button
                type="button"
                onClick={async () => {
                  const shareUrl = user ? appendRefToUrl(window.location.href, user.user_id) : window.location.href;
                  const shareData = {
                    title: listing.title,
                    text: `${listing.title} on AfriMarket`,
                    url: shareUrl,
                  };
                  try {
                    if (navigator.share) {
                      await navigator.share(shareData);
                    } else {
                      await navigator.clipboard.writeText(shareUrl);
                      toast.success("Link copied");
                    }
                  } catch (err) {
                    if (err?.name !== "AbortError") {
                      try {
                        await navigator.clipboard.writeText(shareUrl);
                        toast.success("Link copied");
                      } catch { /* ignore */ }
                    }
                  }
                }}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-[#C05A3C]"
                data-testid="listing-share-btn"
                aria-label="Share listing"
              >
                <Share2 className="w-4 h-4" /> Share
              </button>
            </div>
            <h1 className="font-display text-4xl leading-tight" data-testid="listing-title">{listing.title}</h1>
            {plyxSource && (
              <Link
                to={`/plyx/u/${plyxSource.handle}`}
                data-testid="plyx-attribution-chip"
                className="mt-3 inline-flex items-center gap-2 text-xs font-medium bg-gradient-to-r from-[#FF2D7A]/10 via-[#FFB42D]/10 to-[#00E0C7]/10 border border-[#FF2D7A]/25 rounded-full pl-1.5 pr-3 py-1 hover:from-[#FF2D7A]/15 hover:via-[#FFB42D]/15 hover:to-[#00E0C7]/15 transition-colors"
                title={`You arrived here via @${plyxSource.handle}'s Plyx video`}
              >
                <span className="w-5 h-5 rounded-full bg-white/80 flex items-center justify-center overflow-hidden text-[10px] font-bold text-[#0A0A0A]">
                  {plyxSource.avatar_url
                    ? <img src={plyxSource.avatar_url} alt={plyxSource.handle} className="w-full h-full object-cover"/>
                    : (plyxSource.display_name?.slice(0, 1) || plyxSource.handle?.slice(0, 1) || "P")}
                </span>
                <span className="text-[#1A2024]">via</span>
                <span className="font-semibold text-[#1A2024]">@{plyxSource.handle}</span>
                <span className="text-muted-foreground">on Plyx</span>
              </Link>
            )}
            {listing.is_curated && (
              <div className="mt-3 inline-flex items-start gap-2 text-sm bg-[#C05A3C]/10 border border-[#C05A3C]/30 rounded-lg px-3 py-2" data-testid="curated-badge">
                <span className="text-base leading-none mt-0.5">🌍</span>
                <div className="text-[#1A2024]">
                  <div className="font-semibold">Curated by AfriMarket</div>
                  <div className="text-xs text-muted-foreground">
                    Hand-picked by our team · Ships in <strong>{listing.estimated_shipping_min_days || 14}–{listing.estimated_shipping_max_days || 28} days</strong> from supplier
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center gap-1 text-sm text-muted-foreground mt-3">
              <MapPin className="w-4 h-4"/> {listing.city ? `${listing.city}, ` : ""}{listing.country}
            </div>

            <div className="mt-6 flex items-baseline gap-3 flex-wrap">
              <span className="font-display text-5xl text-[#C05A3C]" data-testid="listing-price-display">
                {formatPrice(effectivePrice, listing.currency) || `${currencySymbol(listing.currency)}${effectivePrice}`}
              </span>
              {listing.type === "service" && rateBasisSuffix(listing.rate_basis, "long") && <span className="text-sm text-muted-foreground">{rateBasisSuffix(listing.rate_basis, "long")}</span>}
              {isConverted(listing.currency) && (
                <span className="text-sm text-muted-foreground font-sans-ui" data-testid="listing-price-original">
                  · listed at {currencySymbol(listing.currency)}{listing.price}
                </span>
              )}
            </div>
            {listing.is_curated && (
              <div className="mt-4 rounded-lg border border-[#C05A3C]/20 bg-[#FAF7F2] p-4 space-y-1.5" data-testid="price-breakdown">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Sale price{qty > 1 ? ` × ${qty}` : ""}</span>
                  <span className="font-semibold text-[#1A2024]" data-testid="pb-sale-price">
                    {formatPrice(effectivePrice * qty, listing.currency) || `${sym}${(effectivePrice * qty).toFixed(2)}`}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Truck className="w-3.5 h-3.5" />
                    Shipping{shipCountry ? ` to ${(SHIPPING_COUNTRIES.find((c) => c.code === shipCountry)?.name) || shipCountry}` : ""}
                  </span>
                  <span className="font-semibold text-[#1A2024]" data-testid="pb-shipping">
                    {shipQuoting
                      ? "…"
                      : shipQuote?.available
                        ? (formatPrice(shipQuote.shipping, listing.currency) || `${sym}${Number(shipQuote.shipping).toFixed(2)}`)
                        : "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-[#C05A3C]/15">
                  <span className="font-semibold text-[#1A2024]">Total to pay (incl. shipping)</span>
                  <span className="font-display text-2xl text-[#C05A3C]" data-testid="pb-total">
                    {shipQuote?.available
                      ? (formatPrice(effectivePrice * qty + Number(shipQuote.shipping), listing.currency) || `${sym}${(effectivePrice * qty + Number(shipQuote.shipping)).toFixed(2)}`)
                      : (formatPrice(effectivePrice * qty, listing.currency) || `${sym}${(effectivePrice * qty).toFixed(2)}`)}
                  </span>
                </div>
              </div>
            )}
              <div className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-[#4A5D23] bg-[#4A5D23]/10 rounded-full px-3 py-1.5" data-testid="listing-local-ccy-chip">
                <Wallet className="w-4 h-4"/>
                Pay in {displayCurrency} · no FX, no conversion fees
              </div>
            )}
            {(() => {
              const s = highStreetSavings(listing.price, listing.category, { onlyIfCurated: true, is_curated: listing.is_curated });
              if (!s) return null;
              const savingsLabel = formatPrice(s.savings, listing.currency) || `${s.savings.toFixed(0)}`;
              const retailLabel = formatPrice(s.retail, listing.currency) || `${s.retail.toFixed(0)}`;
              return (
                <div
                  className="mt-3 ml-2 inline-flex items-center gap-2 text-sm font-semibold text-[#3A4A1C] bg-[#4A5D23]/12 rounded-full px-3 py-1.5"
                  data-testid="listing-savings-chip"
                  title={`Typical UK high-street price ${retailLabel}`}
                >
                  <TrendingDown className="w-4 h-4" />
                  Save {savingsLabel} · {s.percent}% off UK high street
                </div>
              );
            })()}
            {(() => {
              const proof = socialProofBadge(listing);
              if (!proof) return null;
              const Icon = proof.kind === "sold" ? Flame : proof.kind === "views" ? Eye : Sparkles;
              const cls = proof.kind === "sold"
                ? "text-[#C05A3C] bg-[#C05A3C]/10"
                : proof.kind === "views"
                ? "text-[#1A2024] bg-[#1A2024]/8"
                : "text-[#4A5D23] bg-[#4A5D23]/10";
              return (
                <div
                  className={`mt-3 ml-2 inline-flex items-center gap-2 text-sm font-semibold rounded-full px-3 py-1.5 ${cls}`}
                  data-testid="listing-social-proof-chip"
                >
                  <Icon className="w-4 h-4" /> {proof.label}
                </div>
              );
            })()}

            <ServiceEstimator listing={listing} />

            <p className="mt-6 text-base leading-relaxed text-foreground/90 whitespace-pre-line font-sans-ui" data-testid="listing-description">
              {listing.description}
            </p>

            {/* CJ product options — selectable variants (e.g. wig length /
                colour) each with its own price. Selecting one updates the
                price shown and is passed through to checkout. */}
            {listing.type === "product" && (listing.variants?.length > 0) && (
              <div className="mt-6" data-testid="listing-variant-selector">
                <div className="label-overline text-muted-foreground mb-2">
                  {listing.variants.length > 1 ? "Choose an option" : "Option"}
                </div>
                <div className="flex flex-wrap gap-2">
                  {listing.variants.map((v, i) => {
                    const active = selectedVariant?.vid === v.vid;
                    return (
                      <button
                        key={v.vid || i}
                        type="button"
                        onClick={() => setSelectedVariant(v)}
                        data-testid={`variant-option-${i}`}
                        className={`px-3 py-2 rounded-md border text-left transition-colors ${
                          active
                            ? "border-[#C05A3C] bg-[#C05A3C]/10 ring-1 ring-[#C05A3C]"
                            : "border-black/15 hover:border-black/40"
                        }`}
                      >
                        <div className="text-sm font-semibold font-sans-ui">{v.name}</div>
                        <div className="text-xs text-[#C05A3C] font-semibold">
                          {formatPrice(v.price, listing.currency) || `${currencySymbol(listing.currency)}${v.price}`}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Product variants — sizes / colours / condition. Only shown
                if seller filled them in (product listings) and there aren't
                CJ variants above. */}
            {listing.type === "product" && !(listing.variants?.length > 0) && ((listing.sizes?.length || 0) + (listing.colors?.length || 0) > 0 || listing.condition) && (
              <div className="mt-6 space-y-3" data-testid="listing-variants">
                {listing.sizes?.length > 0 && (
                  <div>
                    <div className="label-overline text-muted-foreground mb-2">Sizes available</div>
                    <div className="flex flex-wrap gap-1.5">
                      {listing.sizes.map((s, i) => (
                        <span key={`s-${i}`} className="inline-flex items-center px-2.5 h-7 rounded-md text-xs font-sans-ui font-semibold bg-[#1A2024] text-white" data-testid={`size-chip-${i}`}>
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {listing.colors?.length > 0 && (
                  <div>
                    <div className="label-overline text-muted-foreground mb-2">Colours available</div>
                    <div className="flex flex-wrap gap-1.5">
                      {listing.colors.map((c, i) => (
                        <span key={`c-${i}`} className="inline-flex items-center px-2.5 h-7 rounded-md text-xs font-sans-ui font-semibold bg-[#C05A3C] text-white" data-testid={`color-chip-${i}`}>
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {listing.condition && (
                  <div>
                    <div className="label-overline text-muted-foreground mb-2">Condition</div>
                    <Badge variant="outline" className="text-xs font-semibold" data-testid="condition-badge">
                      {{ "new": "Brand new", "like_new": "Like new", "used": "Used", "refurbished": "Refurbished" }[listing.condition] || listing.condition}
                    </Badge>
                  </div>
                )}
              </div>
            )}

            {/* Seller block */}
            <div className="mt-8 p-5 bg-white border border-black/10 rounded-lg">
              <div className="flex items-center gap-3">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={listing.owner_picture || undefined}/>
                  <AvatarFallback className="bg-[#233348] text-white">{listing.owner_name?.slice(0,1)}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="font-semibold font-sans-ui flex items-center gap-1">
                    {listing.owner_name}
                    {listing.owner_verified_badge && <BadgeCheck className="w-4 h-4 text-[#C05A3C]" title="Verified seller"/>}
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#2A3D36]"/> KYC verified seller</div>
                  {listing.owner_rating_count > 0 && (
                    <div className="mt-1" data-testid="seller-inline-rating">
                      <StarRating value={listing.owner_rating_avg || 0} reviewCount={listing.owner_rating_count} showValue size={13} />
                    </div>
                  )}
                  {listing.owner_id && listing.owner_shop_open && (
                    <Link to={`/shop/${listing.owner_shop_slug || listing.owner_id}`} data-testid="visit-shop-link" className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-[#C05A3C] hover:underline">
                      Visit shop →
                    </Link>
                  )}
                </div>
              </div>
              {!isOwner ? (
                <div className="mt-5 space-y-3">
                  {(() => {
                    const isService = listing.type === "service";
                    const isCurated = !!listing.is_curated;
                    const stockNum = Number.isFinite(+listing.stock) ? +listing.stock : null;
                    // Hide selector entirely for services & one-off products with no stock tracking.
                    if (isService) return null;
                    if (!isCurated && (stockNum === null || stockNum <= 1)) return null;
                    const maxQty = isCurated ? 10 : Math.max(1, Math.min(stockNum, 99));
                    const unit = effectivePrice || Number(listing.price) || 0;
                    const total = (unit * qty).toFixed(2);
                    const dec = () => setQty((q) => Math.max(1, q - 1));
                    const inc = () => setQty((q) => Math.min(maxQty, q + 1));
                    return (
                      <div
                        className="flex items-center justify-between gap-3 p-3 rounded-md border border-black/10 bg-[#FAF7F2]"
                        data-testid="qty-selector"
                      >
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Quantity</div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {qty > 1 ? <>Total <strong className="text-[#1A1A1A]">{sym}{total}</strong></> : <>Max {maxQty} per order</>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={dec}
                            disabled={qty <= 1}
                            data-testid="qty-decrement"
                            aria-label="Decrease quantity"
                            className="w-9 h-9 rounded-md border border-black/15 flex items-center justify-center hover:bg-black/5 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Minus className="w-4 h-4"/>
                          </button>
                          <input
                            type="number"
                            min={1}
                            max={maxQty}
                            value={qty}
                            onChange={(e) => {
                              const v = parseInt(e.target.value || "1", 10);
                              if (Number.isNaN(v)) return setQty(1);
                              setQty(Math.max(1, Math.min(maxQty, v)));
                            }}
                            data-testid="qty-input"
                            className="w-12 h-9 text-center font-semibold border border-black/15 rounded-md focus:outline-none focus:ring-2 focus:ring-[#C05A3C]/30 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                          <button
                            type="button"
                            onClick={inc}
                            disabled={qty >= maxQty}
                            data-testid="qty-increment"
                            aria-label="Increase quantity"
                            className="w-9 h-9 rounded-md border border-black/15 flex items-center justify-center hover:bg-black/5 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Plus className="w-4 h-4"/>
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                  {listing.is_curated && (
                    <div className="p-3 rounded-md border border-black/10 bg-[#FAF7F2]" data-testid="shipping-selector">
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
                        <Globe className="w-3.5 h-3.5" /> Ship to
                      </div>
                      <select
                        value={shipCountry}
                        onChange={(e) => setShipCountry(e.target.value)}
                        data-testid="shipping-country-select"
                        className="w-full h-10 px-3 rounded-md border border-black/15 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#C05A3C]/30"
                      >
                        <option value="">Select your country…</option>
                        {SHIPPING_COUNTRIES.map((c) => (
                          <option key={c.code} value={c.code}>{c.name}</option>
                        ))}
                      </select>
                      {shipCountry && (
                        <div className="mt-2 text-sm" data-testid="shipping-quote">
                          {shipQuoting ? (
                            <span className="text-muted-foreground flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Calculating shipping…</span>
                          ) : shipQuote?.available ? (
                            <div className="flex items-start gap-1.5 text-[#1A1A1A]">
                              <Truck className="w-4 h-4 mt-0.5 text-[#4A5D23]" />
                              <span>
                                Shipping <strong>{sym}{Number(shipQuote.shipping).toFixed(2)}</strong>
                                {shipQuote.eta && <span className="text-muted-foreground"> · arrives in {shipQuote.eta} days</span>}
                              </span>
                            </div>
                          ) : (
                            <span className="text-[#8a5a1a]" data-testid="shipping-unavailable">
                              {shipQuote?.message || "Shipping rate unavailable for this country."}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {listing.owner_whatsapp && (
                    <a
                      href={`https://wa.me/${listing.owner_whatsapp.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hi ${listing.owner_name?.split(" ")[0] || ""}, I saw your listing "${listing.title}" on AfriMarket and I'm interested. Is it still available?`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="whatsapp-seller-btn"
                      className="w-full flex items-center justify-center gap-2 h-12 rounded-md bg-[#25D366] hover:bg-[#1FB155] text-white font-semibold transition-colors"
                    >
                      <WhatsAppIcon className="w-5 h-5" />
                      Chat on WhatsApp
                    </a>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <Button data-testid="contact-seller-btn" onClick={handleContact} disabled={contactLoading} variant="outline" className="border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white rounded-md h-12">
                      {contactLoading ? <Loader2 className="w-4 h-4 animate-spin"/> : <><MessageCircle className="w-4 h-4 mr-2"/> Message</>}
                    </Button>
                    <Button data-testid="buy-now-btn" onClick={handleBuy} disabled={checkoutLoading} className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-md h-12">
                      {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin"/> : <><ShoppingBag className="w-4 h-4 mr-2"/> Pay with card</>}
                    </Button>
                  </div>
                  {/* Promo code input — fold-out, small footprint */}
                  <details className="text-sm" data-testid="promo-code-toggle">
                    <summary className="cursor-pointer text-[#C05A3C] hover:underline select-none">
                      Have a promo code?
                    </summary>
                    <div className="mt-2 flex items-stretch gap-2">
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        placeholder="WELCOME10"
                        className="flex-1 px-3 py-2 border border-black/15 rounded-md text-sm uppercase font-mono focus:outline-none focus:ring-2 focus:ring-[#C05A3C]/30"
                        data-testid="promo-code-input"
                      />
                      <button
                        type="button"
                        onClick={checkPromo}
                        disabled={promoChecking || !promoCode.trim()}
                        className="px-3 bg-[#1A2024] text-white text-xs font-semibold rounded-md hover:bg-[#1A2024]/90 disabled:opacity-50"
                        data-testid="promo-code-apply"
                      >
                        {promoChecking ? "…" : "Apply"}
                      </button>
                    </div>
                    {promoApplied?.valid && (
                      <div className="mt-2 p-2 bg-[#4A5D23]/10 text-[#4A5D23] rounded text-xs" data-testid="promo-code-applied">
                        ✅ <strong>{promoApplied.code}</strong> applied — you save {sym}{promoApplied.discount.toFixed(2)}. New total: <strong>{sym}{promoApplied.final_amount.toFixed(2)}</strong>
                        {typeof promoApplied.used_count === "number" && promoApplied.used_count > 0 && (
                          <div className="mt-1 text-[10px] text-[#C05A3C] font-medium">
                            🔥 Used {promoApplied.used_count} times{promoApplied.max_uses ? ` · ${promoApplied.max_uses - promoApplied.used_count} left at this price` : ""}
                          </div>
                        )}
                      </div>
                    )}
                  </details>
                  <div className="flex items-center gap-2 my-1">
                    <div className="h-px bg-black/10 flex-1"/>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">or</span>
                    <div className="h-px bg-black/10 flex-1"/>
                  </div>
                  <PayPalCheckoutButton listing={listing} qty={qty} variantVid={selectedVariant?.vid} shipCountry={shipCountry}/>
                </div>
              ) : (
                <div className="mt-5 space-y-3">
                  {(() => {
                    const unit = Number(listing.price) || 0;
                    const isPro = !!listing.owner_is_pro;
                    const pct = isPro ? 0.05 : (listing.type === "service" ? 0.10 : 0.08);
                    const fee = unit * pct;
                    const takeHome = unit - fee;
                    return (
                      <div
                        className="p-4 rounded-md border border-[#4A5D23]/25 bg-[#4A5D23]/5"
                        data-testid="seller-payout-estimate"
                      >
                        <div className="text-[10px] uppercase tracking-wider text-[#4A5D23] font-semibold">Your take-home per sale</div>
                        <div className="font-display text-2xl text-[#1A2024] mt-1" data-testid="seller-payout-estimate-amount">
                          ≈ {sym}{takeHome.toFixed(2)}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          From {sym}{unit.toFixed(2)} list price · AfriMarket keeps {sym}{fee.toFixed(2)} ({(pct * 100).toFixed(0)}% {isPro ? "Pro rate" : "platform fee"})
                          {!isPro && (
                            <> · <Link to="/pro" className="text-[#C05A3C] hover:underline">upgrade to Pro</Link> for 5%</>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  <div className="text-sm text-muted-foreground">This is your listing. <Link to="/dashboard/my-listings" className="text-[#C05A3C] hover:underline">Manage in dashboard</Link></div>
                </div>
              )}
            </div>

            <div className="mt-4 text-xs text-muted-foreground flex items-center gap-2">
              <ShieldCheck className="w-3 h-3"/> Always meet in safe locations or use the in-app payment for buyer protection.
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              {listing.type === "service" ? "10%" : "8%"} platform fee included on successful sale (covers payments + buyer protection).
            </div>
            {user && !isOwner && (
              <button
                type="button"
                data-testid="report-listing-btn"
                onClick={() => navigate(`/complaints/new?listing_id=${id}&respondent_id=${listing.owner_id}`)}
                className="mt-3 text-xs text-muted-foreground hover:text-[#C05A3C] underline inline-flex items-center gap-1"
              >
                <Flag className="w-3 h-3" /> Report this listing
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Reviews section — pulled out of container-narrow so it breathes
          on wide screens (the former "reviews look squashed" complaint). */}
      <div className="-mx-4 sm:-mx-6 lg:-mx-8 mt-16 bg-gradient-to-b from-[#FBF9F4] to-white border-y border-black/5 py-12">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-12">
          <ListingReviews listing={listing} />
        </div>
      </div>
    </div>
  );
};

export default ListingDetail;
